import React from 'react'
import styled from 'styled-components'
import DataTable from 'react-data-table-component'
import { Star, ArrowDown } from 'react-feather'
import { Spinner, UncontrolledTooltip } from 'reactstrap'
import { useIntl, FormattedMessage } from 'react-intl'

import { Loading } from '../styles'

const customStyles = {
  headRow: {
    style: {
      backgroundColor: '#f8f8f8',
    },
  },
}

const starNumberColors = [
  'rgba(115, 103, 240, 0.10)',
  'rgba(115, 103, 240, 0.20)',
  'rgba(115, 103, 240, 0.30)',
  'rgba(115, 103, 240, 0.40)',
  'rgba(115, 103, 240, 0.50)',
]

const starColumnElem = number => (
  <div
    className="d-flex align-items-center justify-content-center"
    style={{ gap: '0.2em' }}
  >
    <div>{number}</div>
    <Star size="12" color="#767676" fill="#767676" />
  </div>
)

const locationCell = (row, index) => {
  return (
    <LocationDiv>
      <div>
        <span id={`location-name-cell-${index}`}>{row.location}</span>
        {row?.storecode && (
          <span
            className="storecode-tag"
            id={`location-storecode-cell-${index}`}
          >
            {row.storecode}
          </span>
        )}
      </div>
      <div className="text-muted" id={`location-address-cell-${index}`}>
        {row.address} - {row.city}
      </div>
      <UncontrolledTooltip target={`location-name-cell-${index}`}>
        {row.location}
      </UncontrolledTooltip>
      <UncontrolledTooltip target={`location-address-cell-${index}`}>
        {row.address} - {row.city}
      </UncontrolledTooltip>
      {row?.storecode && (
        <UncontrolledTooltip target={`location-storecode-cell-${index}`}>
          {row.storecode}
        </UncontrolledTooltip>
      )}
    </LocationDiv>
  )
}

function getColor(row, key) {
  const rows = {
    onestar: row.onestar,
    twostar: row.twostar,
    threestar: row.threestar,
    fourstar: row.fourstar,
    fivestar: row.fivestar,
  }

  const colors = {}

  const sortedRows = Object.entries(rows).sort((a, b) => a[1] - b[1])

  sortedRows.forEach(([key, value], index, array) => {
    const previousKey = array?.[index - 1]?.[0]
    const previousValue = array?.[index - 1]?.[1]
    if (value === previousValue) return (colors[key] = colors[previousKey])
    colors[key] = starNumberColors[index]
  })

  return colors[key]
}

const starNumberCell = (row, id) => {
  const color = getColor(row, id)
  const isOneStar = id === 'onestar'
  const isFiveStar = id === 'fivestar'
  return (
    <StarNumberDiv
      style={{
        ...(isOneStar && {
          borderTopLeftRadius: '6px',
          borderBottomLeftRadius: '6px',
          marginLeft: '0.7em',
        }),
        ...(isFiveStar && {
          borderTopRightRadius: '6px',
          borderBottomRightRadius: '6px',
          marginRight: '0.7em',
        }),
        backgroundColor: color,
      }}
    >
      {row[id]}
    </StarNumberDiv>
  )
}

const averageCell = row => {
  return (
    <div
      className="d-flex align-items-center justify-content-center"
      style={{ gap: '0.2em' }}
    >
      <div>{+row.average}</div>
      <Star size="12" color="#767676" />
    </div>
  )
}

const columns = [
  {
    id: 'location',
    name: <FormattedMessage id="Location" />,
    selector: row => row.location,
    width: '30%',
    cell: locationCell,
    sortable: true,
  },
  {
    id: 'reviews',
    name: <FormattedMessage id="Reviews" />,
    selector: row => +row.reviews,
    width: '10%',
    compact: true,
    center: true,
    sortable: true,
  },
  {
    id: 'average',
    name: <FormattedMessage id="Average" />,
    selector: row => +row.average,
    cell: averageCell,
    width: '10%',
    compact: true,
    center: true,
    sortable: true,
  },
  {
    id: 'one-star',
    name: starColumnElem(1),
    selector: row => +row.onestar,
    cell: row => starNumberCell(row, 'onestar'),
    width: '10%',
    compact: true,
    center: true,
    sortable: true,
  },
  {
    id: 'two-star',
    name: starColumnElem(2),
    selector: row => +row.twostar,
    cell: row => starNumberCell(row, 'twostar'),
    width: '10%',
    compact: true,
    center: true,
    sortable: true,
  },
  {
    id: 'three-star',
    name: starColumnElem(3),
    selector: row => +row.threestar,
    cell: row => starNumberCell(row, 'threestar'),
    width: '10%',
    compact: true,
    center: true,
    sortable: true,
  },
  {
    id: 'four-star',
    name: starColumnElem(4),
    selector: row => +row.fourstar,
    cell: row => starNumberCell(row, 'fourstar'),
    width: '10%',
    compact: true,
    center: true,
    sortable: true,
  },
  {
    id: 'five-star',
    name: starColumnElem(5),
    selector: row => +row.fivestar,
    cell: row => starNumberCell(row, 'fivestar'),
    width: '10%',
    compact: true,
    center: true,
    sortable: true,
  },
]

const LoadingData = () => {
  return (
    <Loading>
      <Spinner color="primary" size="lg" />
    </Loading>
  )
}

export const ReputationPage = ({
  loading,
  data,
  total,
  setPageParams,
  setLimitParams,
  setSortParams,
}) => {
  const intl = useIntl()

  const paginationComponentOptions = {
    rowsPerPageText: intl.formatMessage({ id: 'table.pagination' }),
    rangeSeparatorText: intl.formatMessage({ id: 'table.rangeSeparator' }),
    selectAllRowsItem: false,
  }

  return (
    <ReputationDiv>
      <DataTable
        actions
        keyField="reputation"
        className="dataTable-custom"
        columns={columns}
        data={data}
        noHeader
        pagination
        paginationServer
        paginationTotalRows={total}
        sortIcon={<ArrowDown size="4" />}
        onChangePage={setPageParams}
        onChangeRowsPerPage={setLimitParams}
        sortServer
        onSort={setSortParams}
        progressPending={loading}
        progressComponent={<LoadingData />}
        customStyles={customStyles}
        paginationComponentOptions={paginationComponentOptions}
      />
    </ReputationDiv>
  )
}

const ReputationDiv = styled.div`
  display: flex;
  flex-direction: column;
  margin: 2em 0;

  h4 {
    font-size: 1.5em;
    color: #767676;
  }
`

const LocationDiv = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 100%;

  .storecode-tag {
    background-color: #f7fafc;
    color: #4a5568;
    border-radius: 4px;
    padding: 0.1rem 0.5rem;
    font-family: 'Inter', sans-serif;
    font-size: 0.9rem;
    font-weight: 500;
  }

  > div {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;

    span {
      margin-right: 0.5em;
    }
  }
`

const StarNumberDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: calc(100% - 16px);
  transition: height 0.1s;

  &:hover {
    height: calc(100% - 10px);
  }
`
